import { useEffect, useRef, useState } from "react";

/**
 * Facebook-style scroll direction hider: hides once the page has scrolled
 * down past `threshold` px without changing direction, reappears the moment
 * the user scrolls up (a delta past the same threshold — small, not zero, so
 * momentum/rubber-band jitter near a direction change doesn't flicker it),
 * and is always visible at the very top of the page. One state check per
 * animation frame (not per scroll event) keeps it cheap on low-end phones.
 */
export default function useHideOnScroll(threshold = 8, enabled = true) {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return undefined;
    }

    lastY.current = window.scrollY;

    const update = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;

      if (y <= 0) {
        setHidden(false);
        lastY.current = y;
      } else if (delta > threshold) {
        setHidden(true);
        lastY.current = y;
      } else if (delta < -threshold) {
        setHidden(false);
        lastY.current = y;
      }
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, enabled]);

  return enabled && hidden;
}
